<template>
  <div class="w-full pt-2 flex flex-col gap-4">
    <div>
      <h1 class="text-xl font-semibold mb-1">Make Payment</h1>
      <p class="text-sm text-grey-light-600">How much do you want to contribute?</p>
    </div>

    <div class="py-2">
      <hr class="h-px m-0 bg-gray-200 border-0" />
      <CurrencyInputBig v-model="amount" :min="1" :max="maxAmountRand" />
      <hr class="h-px m-0 bg-gray-200 border-0" />
    </div>

    <div class="space-y-2">
      <div class="flex justify-between items-center py-1">
        <p class="text-sm font-bold">Order Total</p>
        <p class="text-sm">{{ displayCurrency(props.order.amountDue) }}</p>
      </div>
      <div class="flex justify-between items-center py-1">
        <p class="text-sm font-bold">Outstanding</p>
        <p class="text-sm">{{ displayCurrency(outstandingAmount) }}</p>
      </div>
    </div>

    <p v-if="validationError" class="text-red-500 text-sm">{{ validationError }}</p>

    <div class="flex gap-2 pt-2">
      <button
        class="w-full bg-white font-bold py-2 rounded-lg border-slate-200 border-2 hover:bg-gray-100"
        @click="emit('cancel')"
      >
        Cancel
      </button>
      <button
        class="w-full bg-teal-600 text-white font-bold py-2 rounded-lg hover:bg-teal-700 disabled:opacity-60 disabled:bg-teal-600"
        :disabled="isNextDisabled"
        @click="onNext"
      >
        Next
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import CurrencyInputBig from '@/components/common/CurrencyInputBig.vue';
import { displayCurrency, toCents, toRand } from '@/utils/format.utils';
import { round } from '@/utils/lowDash';
import { outstanding, upcomingPayments } from '@/utils/paymentPlan.utils';
import { computed, ref, watch } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';

const props = defineProps<{
  order: ModelOrderResponse;
  upcoming?: { amount: number }[];
}>();

const emit = defineEmits<{
  next: [amountCents: number];
  cancel: [];
}>();

const plan = computed(() => props.order?.plans?.[0]);

const outstandingAmount = computed(() => outstanding(plan.value));

const maxAmountRand = computed(() => round(outstandingAmount.value / 100, 2));

const upcoming = computed(() =>
  props.upcoming?.length ? props.upcoming : plan.value ? upcomingPayments(plan.value as any) : [],
);

const recommendedAmount = computed(() => {
  const payments = upcoming.value;

  if (!payments?.length) {
    return 0;
  }

  const first = payments[0]?.amount ?? 0;
  return first > 100 ? toRand(first) : toRand(payments[1]?.amount ?? 0);
});

const amount = ref(recommendedAmount.value > 0 ? recommendedAmount.value : 0);
const amountSet = ref(amount.value > 0);

watch(recommendedAmount, (val) => {
  if (!amountSet.value && val > 0) {
    amount.value = val;
    amountSet.value = true;
  }
});

const validationError = ref('');

const isNextDisabled = computed(() => {
  if (amount.value < 1) return true;
  if (amount.value > maxAmountRand.value) return true;

  return false;
});

watch(amount, (val) => {
  if (val < 1) {
    validationError.value = 'Amount must be at least R1.00';
  } else if (val > maxAmountRand.value) {
    validationError.value = `Amount cannot exceed outstanding balance of ${displayCurrency(outstandingAmount.value)}`;
  } else {
    validationError.value = '';
  }
});

const onNext = () => {
  if (!isNextDisabled.value) {
    emit('next', toCents(amount.value));
  }
};
</script>
